<template>
  <div class="setting-def-form">
    <div v-if="!visibleDefs.length" class="text-muted small">
      Нет настроек для отображения.
    </div>
    <div v-else class="setting-def-grid" :style="{ '--setting-def-columns': columns }">
      <div
        v-for="def in visibleDefs"
        :key="def.id || def.settingKey + ':' + JSON.stringify(def.dependencies || [])"
        :class="['setting-def-item', { 'setting-def-item--full': def.settingType === 'TEXT_AREA' }]"
      >
        <small class="text-muted d-block">{{ def.description || def.settingKey }}</small>

        <template v-if="def.settingType === 'DROP_DOWN'">
          <select
            class="form-select form-select-sm"
            :value="modelValue[def.settingKey]"
            @change="onChange(def, $event.target.value)"
          >
            <option v-for="opt in (def.metadata?.options || [])" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </template>

        <template v-else-if="def.settingType === 'MULTI_SELECT'">
          <select
            class="form-select form-select-sm"
            multiple
            :size="Math.min(8, Math.max(4, (def.metadata?.options || []).length))"
            :value="toArray(modelValue[def.settingKey])"
            @change="onMultiChange(def, $event.target)"
          >
            <option v-for="opt in (def.metadata?.options || [])" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </template>

        <template v-else-if="def.settingType === 'SLIDER'">
          <div class="slider-wrapper">
            <input
              type="range"
              class="form-range"
              :min="def.metadata?.min"
              :max="def.metadata?.max"
              :step="def.metadata?.step"
              :value="modelValue[def.settingKey]"
              @input="onChange(def, Number($event.target.value))"
            />
            <span class="slider-value">{{ modelValue[def.settingKey] }}</span>
          </div>
        </template>

        <template v-else-if="def.settingType === 'CHECKBOX'">
          <div class="form-check form-switch setting-def-switch mt-1">
            <input
              :id="checkboxId(def)"
              class="form-check-input"
              type="checkbox"
              :checked="toBool(modelValue[def.settingKey])"
              @change="onChange(def, $event.target.checked)"
            />
            <label class="form-check-label" :for="checkboxId(def)">
              {{ toBool(modelValue[def.settingKey]) ? 'Включено' : 'Выключено' }}
            </label>
          </div>
        </template>

        <template v-else-if="def.settingType === 'TEXT_AREA'">
          <textarea
            class="form-control form-control-sm"
            rows="3"
            :value="modelValue[def.settingKey]"
            @input="onChange(def, $event.target.value)"
          ></textarea>
        </template>

        <template v-else>
          <input
            type="text"
            class="form-control form-control-sm"
            :value="modelValue[def.settingKey]"
            @input="onChange(def, $event.target.value)"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  // List of stream_setting_definition rows (from /api/stream-settings/by-type)
  defs: {
    type: Array,
    default: () => [],
  },
  // v-model: { settingKey: value }
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  columns: {
    type: Number,
    default: 2,
  },
})
const emit = defineEmits(['update:modelValue'])

function pickByDependencies(arr, settings) {
  // Pick the row whose dependencies match the current settings; fall back
  // to the dependency-less row. Mirrors StreamFormPage's pickDef logic.
  const specific = arr.find(d =>
    Array.isArray(d.dependencies) && d.dependencies.length > 0 &&
    d.dependencies.every(dep =>
      Array.isArray(dep.values) && dep.values.includes(settings[dep.settingKey])
    )
  )
  if (specific) return specific
  return arr.find(d => !Array.isArray(d.dependencies) || d.dependencies.length === 0) || arr[0]
}

const visibleDefs = computed(() => {
  const byKey = new Map()
  for (const def of props.defs) {
    if (!byKey.has(def.settingKey)) byKey.set(def.settingKey, [])
    byKey.get(def.settingKey).push(def)
  }
  const picked = []
  for (const [, arr] of byKey) {
    const def = pickByDependencies(arr, props.modelValue)
    if (def) picked.push(def)
  }
  // Stable ordering by settingType. Checkboxes lead so that, in mixed rows, the
  // compact toggles sit on the left ahead of the wider inputs (groups that are
  // all checkboxes are unaffected). TEXT_AREA stays last (full-width).
  const order = { CHECKBOX: 0, DROP_DOWN: 1, MULTI_SELECT: 2, SLIDER: 3, TEXT_BOX: 4, TEXT_AREA: 5 }
  return picked.sort((a, b) =>
    (order[a.settingType] ?? 999) - (order[b.settingType] ?? 999)
  )
})

function checkboxId(def) {
  return `def-${(def.id || def.settingKey).toString().replace(/\W+/g, '_')}`
}

function toArray(v) {
  if (Array.isArray(v)) return v
  if (v == null || v === '') return []
  return [v]
}

function toBool(v) {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v === 1
  if (typeof v === 'string') return ['true', '1', 'yes', 'on'].includes(v.trim().toLowerCase())
  return Boolean(v)
}

function onChange(def, value) {
  emit('update:modelValue', { ...props.modelValue, [def.settingKey]: value })
}

function onMultiChange(def, target) {
  const selected = Array.from(target.selectedOptions || []).map(o => o.value)
  emit('update:modelValue', { ...props.modelValue, [def.settingKey]: selected })
}
</script>

<style scoped>
.setting-def-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
}

.setting-def-item {
  min-width: 0;
  /* Each item targets ~1/columns of the row but grows to consume any leftover
     space, so a row always fills the full width regardless of how many items
     land on it. */
  flex: 1 1 calc((100% - (var(--setting-def-columns) - 1) * 12px) / var(--setting-def-columns));
  border: 1px solid #e7edf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px 12px;
}

.setting-def-item--full { flex-basis: 100%; }
.setting-def-item small { margin-bottom: 6px; line-height: 1.3; }

.slider-wrapper { display: flex; gap: 10px; align-items: center; }
.slider-wrapper .form-range { flex: 1; }
.slider-wrapper .slider-value { min-width: 28px; white-space: nowrap; font-weight: 600; color: #354b74; }

.setting-def-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  padding-left: 0;
}

.setting-def-switch .form-check-input {
  flex: 0 0 auto;
  width: 2.35rem;
  height: 1.25rem;
  margin: 0;
  border: 1px solid #b9c8de;
  background-color: #d6deea;
  box-shadow: none;
  cursor: pointer;
}

.setting-def-switch .form-check-input:checked {
  border-color: #27815d;
  background-color: #2e9b70;
}

.setting-def-switch .form-check-input:focus {
  border-color: #5198df;
  box-shadow: 0 0 0 0.2rem rgba(30, 103, 183, 0.14);
}

.setting-def-switch .form-check-label {
  color: #43567a;
  cursor: pointer;
  font-size: 13px;
}

@media (max-width: 768px) {
  .setting-def-item { flex-basis: 100%; }
}
</style>
